import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Space, Row } from 'antd';
import Button from '@mui/material/Button';
import {ArrowUpRight} from 'lucide-react';
import Placeholder from '../../components/Placeholder';
import { usePortfolioGroups } from './usePortfolioGroups';

const PortfolioGroups = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { portfolioGroups } = usePortfolioGroups();

  // Identify all unique portfolio IDs across groups
  const uniquePortfolioIds = portfolioGroups.reduce((acc, group) => {
    group.portfolio_ids.forEach((id) => {
      if (!acc.includes(id)) {
        acc.push(id);
      }
    });
    return acc;
  }, []);

  // Get all relevant portfolios from Redux
  const portfolioDocuments = useSelector((state) =>
    state.portfolios.items?.filter((doc) => uniquePortfolioIds.includes(doc.id)) || []
  );

  // Handle comparison navigation for a group
  const handleComparison = (groupId) => {
    const group = portfolioGroups.find((g) => g.group_id === groupId);
    if (!group) return;

    const items = group.portfolio_ids.map((id) => {
      const matchingDoc = portfolioDocuments.find((doc) => doc.id === id);
      return { id, label: matchingDoc?.name || 'Unnamed' };
    });

    const queryIds = items.map((item) => item.id).join(',');
    const queryLabels = items.map((item) => item.label).join(',');
    navigate(`/performance/comparison?ids=${queryIds}&labels=${queryLabels}`);
  };

  if (!portfolioGroups.length) {
    return <Placeholder />;
  }

  return (
    <div className="p-6">
      {/*
        Tailwind Grid:
        - grid-cols-1 for mobile
        - sm:grid-cols-2 for small screens
        - lg:grid-cols-3 for large screens
        - items-stretch makes each grid cell (and thus each card) match the height of the tallest item in that row
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {portfolioGroups.map((group) => (
          <Card
            bordered={false}
            key={group.group_id}
            className="h-full flex flex-col"
          >
              <Row justify={'center'}>
                  <span className={'text-xl font-medium'}>
                      {group.name}
                  </span>
              </Row>
              <Space>
                  <Button variant="text" onClick={() => handleComparison(group.group_id)}>
                    Comparison<sup><ArrowUpRight size={16}/></sup>
                  </Button>
              </Space>
            <Table
              rowKey="id"
              pagination={false}
              dataSource={portfolioDocuments.filter((doc) => group.portfolio_ids.includes(doc.id))}
              className="nowrap-headers transparent no-border"
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Type', dataIndex: 'type', key: 'type' },
              ]}
              // OPTIONAL: If you want the table to stretch vertically, add a className to manage height
              // className="flex-grow"
            />
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PortfolioGroups;
